import {BaseService} from '@esgi/core/service';
import {getUser} from '@esgi/core/authentication';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	filter,
	Observable,
	of,
	switchMap,
	tap,
} from 'rxjs';
import {
	DownloadRequest,
	DownloadTestModel,
	MarkingPeriod,
	StudentWithUnit,
	UnitDataSettings,
	RequestReportModel,
	TestModel,
	ReportDisplayResultsMode,
	SubjectTab,
	Report,
	GradeScale,
	InitRequest,
	RefreshRequest, ReportData,
} from '../types';
import {StudentService} from './student-service';
import {ColorType, DisplayNotTestedType, DisplayType} from '../kit/types';
import {TestService} from './test-service';
import {SubjectService} from './subject-service';
import {V2PagesReportsStudentProgressController} from '@esgi/contracts/esgi';
import {Test} from '../kit/tests-control/types';

export class StudentProgressReportService extends BaseService {
	public readonly students$ = new BehaviorSubject<StudentWithUnit[]>([]);
	public readonly tests$ = new BehaviorSubject<Test[]>([]);
	public readonly selectedTests$ = new BehaviorSubject<Test[]>([]);
	public readonly colorType$ = new BehaviorSubject<ColorType>(ColorType.Colored);
	public readonly displayType$ = new BehaviorSubject<DisplayType>(DisplayType.Score);
	public readonly subjectTab$ = new BehaviorSubject<SubjectTab>(null);
	public readonly markingPeriods$ = new BehaviorSubject<MarkingPeriod[]>([]);
	public readonly displayNotTestedType$ = new BehaviorSubject<DisplayNotTestedType>(DisplayNotTestedType.NT);
	public readonly showBaseline$ = new BehaviorSubject<boolean>(false);
	public readonly carryForward$ = new BehaviorSubject<boolean>(false);
	public readonly includeGrades$ = new BehaviorSubject<boolean>(false);
	public readonly report$ = new BehaviorSubject<Report>(null);
	public readonly unitDataSettings$ = new BehaviorSubject<UnitDataSettings>(null);
	public readonly testResultsCorrectVerbiage$ = new BehaviorSubject<string>('');
	public readonly teacherName$ = new BehaviorSubject<string>('');
	public readonly gradeScales$ = new BehaviorSubject<GradeScale[]>([]);
	public readonly initCompleted$ = new BehaviorSubject<boolean>(false);

	public readonly busy$ = new BehaviorSubject<boolean>(false);

	public readonly valid$: Observable<boolean> = this.completeOnDestroy(
		combineLatest([this.students$, this.selectedTests$, this.subjectTab$])
	).pipe(
		debounceTime(100),
		switchMap(([students, selectedTests, subjectTab]) => {
			return of(Boolean(students.length) && Boolean(selectedTests.length) && Boolean(subjectTab));
		}),
	);

	protected currentUser = getUser();
	public studentService = new StudentService();
	public subjectService = new SubjectService();
	public testService = new TestService(this.currentUser, this.subjectTab$, this.unitDataSettings$, this.subjectService.subjects$);
	protected studentProgressController = new V2PagesReportsStudentProgressController();

	constructor() {
		super();

		this.completeOnDestroy(
			combineLatest([this.students$])
		).pipe(
			debounceTime(100),
			tap(([students]) => this.unitDataSettings$.next({
				students: students.map(({studentId, unitData}) => {
					const student = this.studentService.students$.value.find(
						({id}) => id === studentId,
					);
					return {...student, unitData};
				}),
				lecturer: {
					firstName: this.currentUser.firstName,
					lastName: this.currentUser.lastName,
					title: '',
				},
			})),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([this.testService.busy, this.studentService.busy, this.subjectService.busy])
		).pipe(
			debounceTime(100),
			tap(([busy]) => this.busy$.next(busy)),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([this.studentService.groups$, this.studentService.classes$, this.studentService.students$])
		);

		this.completeOnDestroy(
			this.subjectService.loaded$
		).pipe(tap((value) => {
			if (value) {
				this.subjectTab$.next(this.subjectService.getByIndex(0));
			}
		})).subscribe();

		this.completeOnDestroy(
			combineLatest([this.unitDataSettings$, this.subjectTab$, this.selectedTests$, this.subjectService.subjects$, this.initCompleted$])
		).pipe(
			debounceTime(100),
			filter(([students, subject, tests]) => {
				return subject !== null && students !== null && students?.students.length !== 0 && tests.length !== 0;
			}),
			tap(([students, subjectTab, selectedTests, allSubjects, initCompleted]) => {
				if (initCompleted) {
					return;
				}

				const requestReportModel: RequestReportModel = {
					includeGrades: false,
					subjectID: subjectTab.id || allSubjects[0].id,
					subjectType: subjectTab.type,
					subjectLevel: subjectTab.level,
					displayZeroIfNotTested: false,
					showGradeColors: false,
					carryScoresForward: false,
					reportDisplayResultsMode: ReportDisplayResultsMode.Score,
					zip: false,
					studentIDs: [...students.students.map((student) => student.id)],
					selectedTests: [...selectedTests.map((test) => {
						const subject = allSubjects.find(x => x.id === test.subjectID);
						const testModel: TestModel = {
							testID: test.id,
							subjectID: subject.id,
							subjectType: subject.type,
							subjectLevel: subject.level,
						};
						return testModel;
					})],
				};

				const model: InitRequest = {
					userID: this.currentUser?.userID,
					userType: this.currentUser?.userType,
					districtID: this.currentUser?.districtID,
					schoolID: this.currentUser?.schoolID,
					globalSchoolYearID: this.currentUser?.globalSchoolYearID,
					requestReportModel: requestReportModel,
				};

				this.studentProgressController.init(model).subscribe((response) => {
					if (!response.isSuccess) {
						return;
					}
					const result = response.value;
					this.colorType$.next(result.showGradeColors ? ColorType.Colored : ColorType.BlackAndWhite);
					this.displayNotTestedType$.next(result.displayZeroIfNotTested ? DisplayNotTestedType.Zero : DisplayNotTestedType.NT);
					this.displayType$.next(this.getDisplayType(result.reportDisplayResultsMode));
					this.carryForward$.next(result.carryScoresForward);
					this.showBaseline$.next(result.showBaseline);
					this.testResultsCorrectVerbiage$.next(result.testResultsCorrectVerbiage);
					this.teacherName$.next(result.teacherName);

					if (result.report) {
						const localMP = [];
						localMP.push({index: 0, title: 'B'});
						for (let i = 0; i < result.report.trackDates.length; i++) {
							localMP.push({index: i + 1, title: (i + 1).toString()});
						}
						this.markingPeriods$.next(localMP);

						this.report$.next(result.report);
						this.gradeScales$.next(result.report.gradeScales);
					}
				});
				this.initCompleted$.next(true);
			}),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([this.unitDataSettings$, this.subjectTab$, this.selectedTests$, this.subjectService.subjects$, this.initCompleted$])
		).pipe(
			debounceTime(100),
			filter(([students, subject, tests]) => {
				return subject !== null && students !== null && students?.students.length !== 0 && tests.length !== 0;
			}),
			tap(([students, subjectTab, selectedTests, allSubjects, initCompleted]) => {
				if (!initCompleted) {
					return;
				}
				this.updateReport(this.requestReportModel);
			}),
		).subscribe();
	}

	public destroy() {
		super.destroy();
		this.studentService.destroy();
		this.testService.destroy();
		this.subjectService.destroy();
	}

	public override dispose() {
		super.dispose();
		this.studentProgressController.dispose();
	}

	public setStudents(value: StudentWithUnit[]) {
		this.students$.next(value);
	}

	public setSubjectTab(value: SubjectTab) {
		this.subjectTab$.next(value);
	}

	public setSelectedTests(value: number[]) {
		const selectedTestLocal = this.testService.tests$.value.filter((test) => {
			return value.includes(test.id);
		});
		this.selectedTests$.next(selectedTestLocal);
	}

	public setColorType(value: ColorType) {
		this.colorType$.next(value);
		this.updateReport({...this.requestReportModel, showGradeColors: value === ColorType.Colored});
	}

	public setDisplayType(value: DisplayType) {
		this.displayType$.next(value);
		this.updateReport({...this.requestReportModel, reportDisplayResultsMode: this.getDisplayTypeNumber(value)});
	}

	public setDisplayNotTested(value: DisplayNotTestedType) {
		this.displayNotTestedType$.next(value);
		this.updateReport({...this.requestReportModel, displayZeroIfNotTested: value === DisplayNotTestedType.Zero});
	}

	public setIncludeGrades(value: boolean) {
		this.includeGrades$.next(value);
	}

	public setShowBaseline(value: boolean) {
		this.showBaseline$.next(value);
		this.studentProgressController.showBaseline({value}).subscribe();
	}

	public setCarryForward(value: boolean) {
		this.carryForward$.next(value);
		this.updateReport({...this.requestReportModel, carryScoresForward: value});
	}

	public generateReport() {
		this.updateReport({...this.requestReportModel});
	}

	public downloadPdf(zip: boolean) {
		const date = new Date();
		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();
		const filename = 'Student_Progress_Report' + year + '-' + month + '-' + day + '.' + (zip ? 'zip' : 'pdf');
		const requestReportModel: DownloadRequest = {
			subjectID: this.subjectTab$.value?.id,
			subjectLevel: this.subjectTab$.value?.level,
			subjectType: this.subjectTab$.value?.type,
			showGradeColors: this.colorType$?.value === ColorType.Colored,
			carryScoresForward: this.carryForward$?.value,
			displayZeroIfNotTested: this.displayNotTestedType$?.value === DisplayNotTestedType.Zero,
			reportDisplayResultsMode: this.getDisplayTypeNumber(this.displayType$.value),
			includeGrades: this.includeGrades$.value,
			selectedTests: [...this.selectedTests$?.value.map((test) => {
				const subject = this.subjectService.subjects$.value.find(x => x.id === test.subjectID);
				const testModel: DownloadTestModel = {
					testID: test.id,
					subjectID: subject.id,
					subjectType: subject.type,
					subjectLevel: subject.level,
				};
				return testModel;
			})],
			zip: zip,
			studentIDs: [...this.unitDataSettings$.value?.students?.map((student) => student.id)],
		};
		this.busy$.next(true);
		this.httpClient.ESGIApi.file<ReportData>('/v2/modules/pages/student-progress', 'download-pdf', filename,
			{
				userID: this.currentUser?.userID,
				userType: this.currentUser?.userType,
				districtID: this.currentUser?.districtID,
				schoolID: this.currentUser?.schoolID,
				globaSchoolYearID: this.currentUser?.globalSchoolYearID,
				requestReportModel: requestReportModel,
			})
			.subscribe({
				complete: () => {
					this.busy$.next(false);
				},
			});
	}

	private updateReport(model: RequestReportModel) {
		const request: RefreshRequest = {
			userID: this.currentUser?.userID,
			userType: this.currentUser?.userType,
			districtID: this.currentUser?.districtID,
			schoolID: this.currentUser?.schoolID,
			globalSchoolYearID: this.currentUser?.globalSchoolYearID,
			requestReportModel: model,
		};
		this.busy$.next(true);
		return this.studentProgressController.refresh(request).subscribe({
			next: (response) => {
				if (response.value?.report) {
					this.report$.next(response.value.report);
					const localMP = [];
					localMP.push({index: 0, title: 'B'});
					for (let i = 0; i < response.value?.report.trackDates.length; i++) {
						localMP.push({index: i + 1, title: (i + 1).toString()});
					}
					this.markingPeriods$.next(localMP);
				}
			},
			complete: () => {
				this.busy$.next(false);
			},
		});
	}

	private get requestReportModel(): RequestReportModel {
		const studentIds = this.unitDataSettings$.value?.students?.map((student) => student.id) || [];
		return {
			includeGrades: this.includeGrades$?.value,
			carryScoresForward: this.carryForward$?.value,
			displayZeroIfNotTested: this.displayNotTestedType$?.value === DisplayNotTestedType.Zero,
			reportDisplayResultsMode: this.getDisplayTypeNumber(this.displayType$.value),
			selectedTests: [...this.selectedTests$?.value.map((test) => {
				const subject = this.subjectService.subjects$.value.find(x => x.id === test.subjectID);
				const testModel: TestModel = {
					testID: test.id,
					subjectID: subject.id,
					subjectType: subject.type,
					subjectLevel: subject.level,
				};
				return testModel;
			})],
			showGradeColors: this.colorType$?.value === ColorType.Colored,
			studentIDs: studentIds,
			subjectID: this.subjectTab$.value?.id,
			subjectLevel: this.subjectTab$.value?.level,
			subjectType: this.subjectTab$.value?.type,
			zip: false,
		};
	}

	private getDisplayType(displayType: number) {
		switch(displayType) {
			case 0:
				return DisplayType.Score;
			case 1:
				return DisplayType.Percent;
			case 2:
				return DisplayType.Grade;
			default:
				return DisplayType.Score;
		}
	}

	private getDisplayTypeNumber(displayType: DisplayType): number {
		switch(displayType) {
			case DisplayType.Score:
				return 0;
			case DisplayType.Percent:
				return 1;
			case DisplayType.Grade:
				return 2;
			default:
				return 0;
		}
	}
}
